import React, { useEffect, useState } from 'react';
import { Tabs, Tab, Paper, Grid, TextField, IconButton, Button, Box, CircularProgress, Autocomplete, Tooltip, RadioGroup, FormControlLabel, Radio, FormGroup, Typography, Checkbox, Divider } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSnackbar } from 'notistack';
import { globalGetService, globalPutService } from '../../../utils/globalApiServices';
import { useParams } from 'react-router-dom';
import '../styles/rentalRate.scss';
import { getDayLabel, InvoiceDays } from '..';
import { getBankDropdown, getInvoiceConfigApi, UpdateInvoiceConfigApi } from '../apiService';
import { checkApiStatus } from '../../../utils';
import { PermissionWrapper } from '../../../wrappers';
import { regexConstants } from '../../../constants/regEx';
import { trackActivity } from '../../../utils/mixpanel';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { backendDateFormat, fieldDateFormat } from '../../../constants';
import moment from 'moment';
import { useSelector } from 'react-redux';
import LessMoreTextFieldComp from '../../../shared_elements/components/LessMoreTextFieldComp';
import AutocompleteCreatable from '../../../shared_elements/components/AutocompleteCreatable';

const InvoiceConfiguration = ({ isListLoading }) => {
    const { leaseSlug } = useParams();
    const params = { leaseSlug: leaseSlug };
    const { enqueueSnackbar } = useSnackbar();
    const [tabIndex, setTabIndex] = useState(0);
    const [editMode, setEditMode] = useState(false);
    const [isLoading, setLoading] = useState(true);
    const [invoiceConfig, setInvoiceConfig] = useState({});
    const [bank, setBank] = useState([])
    const [dropDownloader, setDropDownLoader] = useState(false)
    const [error, setError] = useState({});
    const { leaseBasicDetails } = useSelector((state) => state.leases);

    useEffect(() => {
        getInvoiceConfig();
    }, []);

    useEffect(() => {
        const id = leaseBasicDetails?.lessor?.id;
        if (!id) return;

        getBankDetails();
    }, [leaseBasicDetails?.lessor?.id]);

    const getInvoiceConfig = () => {
        setLoading(true);
        globalGetService(getInvoiceConfigApi(params), {}).then(response => {
            setLoading(false);
            if (checkApiStatus(response)) {
                setInvoiceConfig(response?.data?.data)
                getBankDetails(response?.data?.data?.rental_invoice_bank?.id)
            }
        });
    }
   
    const getBankDetails = (include_bank_id) => {
        setDropDownLoader(true)
        globalGetService(getBankDropdown(leaseBasicDetails?.lessor?.id, include_bank_id))
            .then(response => {
                setDropDownLoader(false)
                if (checkApiStatus(response)) {
                    setBank(response.data.data)
                }
            })
    }
    const onFieldChange = (key, value) => {
        if (key === "is_rental_billing_cycle") {
            setInvoiceConfig((prev) => ({ ...prev, [key]: value === "true" ? true : false }));
        }
        else {
            setInvoiceConfig((prev) => ({ ...prev, [key]: value }));
        }
    };
    const validateDate = (value, fieldLabel) => {
        if (!value) return "";
        const leaseStartDate = moment(leaseBasicDetails?.start_date);
        const leaseEndDate = moment(leaseBasicDetails?.end_date);
        const date = moment(value);

        if (!date.isValid()) {
            return `Please enter valid ${fieldLabel}`;
        }
        if (!leaseStartDate?.isValid() || !leaseEndDate?.isValid()) {
            return "";
        }
        return date.isBetween(leaseStartDate, leaseEndDate, "day", "[]")
            ? ""
            : `${fieldLabel} must be within the lease period`;
    };

    const handleSave = () => {
        let validationInputs = {
            remarks_date: validateDate(invoiceConfig?.remarks_date, "Remarks Date")
        };
        const getMonthValue = (value, key) => {
            const val =
              typeof value === "string"
                ? value
                : value?.label || "Same Month";
          
            if (val === "Same Month") return 1;
          
            return key === "advanced" ? 3 : 2;
          };
        const payload = {
            rental_invoice_generation_day: invoiceConfig?.rental_invoice_generation_day ? invoiceConfig?.rental_invoice_generation_day : null,
            advanced_rental_invoice_generation_day: invoiceConfig?.advanced_rental_invoice_generation_day ? invoiceConfig?.advanced_rental_invoice_generation_day : null,
            rental_invoice_due_month: invoiceConfig?.rental_invoice_due_month ? invoiceConfig?.rental_invoice_due_month : null,
            rental_invoice_due_day: invoiceConfig?.rental_invoice_due_day ? invoiceConfig?.rental_invoice_due_day : null,
            advanced_rental_invoice_due_day: invoiceConfig?.advanced_rental_invoice_due_day ? invoiceConfig?.advanced_rental_invoice_due_day : null,
            rental_invoice_bank: invoiceConfig?.rental_invoice_bank ? invoiceConfig?.rental_invoice_bank?.id : null,
            rental_remarks: invoiceConfig?.rental_remarks ? invoiceConfig?.rental_remarks : null,
            rental_advance_duration: invoiceConfig?.rental_advance_duration ? invoiceConfig?.rental_advance_duration : null,
            reference_number: invoiceConfig?.reference_number ? invoiceConfig?.reference_number : null,
            qis_number: invoiceConfig?.qis_number ? invoiceConfig?.qis_number : null,
            sales_order_number: invoiceConfig?.sales_order_number ? invoiceConfig?.sales_order_number : null,
            remarks_date: invoiceConfig?.remarks_date ? moment(invoiceConfig?.remarks_date).format(backendDateFormat) : null,
            description: invoiceConfig?.description ? invoiceConfig?.description : null,
            is_rental_billing_cycle: invoiceConfig?.is_rental_billing_cycle ? invoiceConfig?.is_rental_billing_cycle : false,
            falling_months: getMonthValue(invoiceConfig?.falling_months),
            advanced_falling_months: getMonthValue(invoiceConfig?.advanced_falling_months, "advanced"),
            arrear_rental_invoice_is_due_day_last_day_month: invoiceConfig?.arrear_rental_invoice_is_due_day_last_day_month ? invoiceConfig?.arrear_rental_invoice_is_due_day_last_day_month : false,
            advanced_rental_invoice_is_due_day_last_day_month: invoiceConfig?.advanced_rental_invoice_is_due_day_last_day_month ? invoiceConfig?.advanced_rental_invoice_is_due_day_last_day_month : false,
            rental_email_cc: invoiceConfig?.rental_email_cc ? invoiceConfig?.rental_email_cc : null,
            billing_generation_day: invoiceConfig?.billing_generation_day ? invoiceConfig?.billing_generation_day : null,
        };
        if (Object.keys(validationInputs).every((k) => { return validationInputs[k] === ""; })) {
            setLoading(true);
            globalPutService(UpdateInvoiceConfigApi(params), payload)
                .then(async (response) => {
                    if (checkApiStatus(response)) {
                        setEditMode(false)
                        getInvoiceConfig();
                        setLoading(false);
                        enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { horizontal: "right", vertical: "top" } });
                        trackActivity('Item Edited', { page_title: `Edit Rental Rates Invoice Configuration`, success_msg: response?.data?.message, item_slug: leaseSlug, payload: payload, response: response?.data })
                    } else {
                        setLoading(false);
                        enqueueSnackbar(response.data.message, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" }, });
                        trackActivity('Item Edited', { page_title: `Edit Rental Rates Invoice Configuration`, error_msg: response?.data?.message, item_slug: leaseSlug, payload: payload, response: response?.data })
                    }
                });
        }
        else {
            setError(validationInputs)
        }
    }
    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: `Rental Rates Invoice Configuration`, page_type: page_type, })
    }

    const emailValue = Array.isArray(invoiceConfig?.rental_email_cc) ? invoiceConfig.rental_email_cc.join(", ") : invoiceConfig?.rental_email_cc || "";
    const leaseStartDay = leaseBasicDetails?.start_date ? Math.min(moment(leaseBasicDetails.start_date).date(), 30) : null;

    return (
        <>

            <Paper className="invoice-config-section">
                {(isLoading || isListLoading) ? (
                    <Box display="flex" justifyContent="center" py={16}>
                        <CircularProgress size={26} />
                    </Box>
                ) : (
                    <>
                        <div className="invoice-config-header-wrap">
                            <Grid container spacing={1}>
                                <Grid size={{ md: 12, xs: 12 }}>
                                    <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "lease_rental_rate", operationType: ['U'] }}>
                                        {!editMode && !isLoading && (
                                            <Tooltip title='Edit'><IconButton size="small" className="invoice-config-edit-btn" onClick={() => { setEditMode(true); handleItemClicked('EditIcon') }} color='primary' > <EditIcon fontSize="small" /> </IconButton></Tooltip>
                                        )}
                                    </PermissionWrapper>
                                </Grid>
                                <Grid container size={{ md: 12, xs: 12 }} spacing={2} className='invoice-config-grid'>
                                    <Grid size={{ md: 12, xs: 6 }}>
                                        <Typography className='invoice-config-text' >Arrear Invoice Configuration</Typography>
                                    </Grid>
                                    <Grid item size={{ md: 2.5, xs: 12 }}>
                                        <Autocomplete
                                            disablePortal
                                            options={InvoiceDays}
                                            getOptionLabel={(option) => option || ""}
                                            isOptionEqualToValue={(option, value) => getDayLabel(option) === value}
                                            value={getDayLabel(invoiceConfig?.rental_invoice_generation_day)}
                                            onChange={(e, value) => onFieldChange("rental_invoice_generation_day", value || '')}
                                            disabled={!editMode}
                                            slotProps={{ listbox: { sx: { maxHeight: 155 } } }}
                                            renderInput={(params) => {
                                                const selectedLabel = params?.inputProps?.value || "";
                                                return (
                                                    <Tooltip title={selectedLabel.length > 23 ? selectedLabel : ""} placement="bottom" arrow ><TextField  {...params} label="Invoice Generation Day" margin="normal" size="small" slotProps={{ inputLabel: { shrink: true } }} /></Tooltip>)
                                            }}
                                        />
                                    </Grid>
                                    <Grid item size={{ md: 2.5, xs: 12 }}>
                                        <Autocomplete
                                            disablePortal
                                            options={['Same Month', 'Next Month']}
                                            slotProps={{ listbox: { sx: { maxHeight: 155 } } }}
                                            value={invoiceConfig.falling_months || 'Same Month'}
                                            onChange={(e, value) =>
                                                onFieldChange(
                                                  "falling_months",
                                                  value || "Same Month"
                                                )
                                              }
                                            disabled={!editMode}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Falling Month"
                                                    margin="normal"
                                                    size="small"
                                                    slotProps={{ inputLabel: { shrink: true } }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item size={{ md: 0.2, xs: 12 }} sx={{ marginTop: '-5px'}}> 
                                        <Divider orientation="vertical" sx={{ height: 50, borderRightWidth: 2,  borderColor: '#c0c3c6' }} /> 
                                    </Grid>
                                    <Grid size={{ md: 2.5, xs: 12 }}>
                                        <TextField
                                            id="rental_invoice_due_day"
                                            value={invoiceConfig?.rental_invoice_due_day}
                                            onChange={(e, value) => onFieldChange("rental_invoice_due_day", e.target.value.trimStart())}
                                            label="Invoice Due Day"
                                            variant="outlined"
                                            size="small"
                                            margin="normal"
                                            slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 2 } }}
                                            fullWidth
                                            disabled={!editMode}
                                        />
                                    </Grid>
                                    <Grid size={{ md: 4, xs: 12 }}>
                                        <FormControlLabel
                                            control={<Checkbox disableRipple size="small" color="primary" disabled={!editMode} onChange={(e) => onFieldChange("arrear_rental_invoice_is_due_day_last_day_month", e.target.checked)} checked={!!invoiceConfig?.arrear_rental_invoice_is_due_day_last_day_month} />}
                                            label="Invoice Due Day falls at last day of the Billing Month"
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container size={{ md: 12, xs: 12 }} spacing={2} className='invoice-config-grid'>
                                    <Grid size={{ md: 12, xs: 12 }}>
                                        <Typography className='invoice-config-text'>Advanced Invoice Configuration</Typography>
                                    </Grid>
                                    <Grid item size={{ md: 2.5, xs: 12 }}>
                                        <Autocomplete
                                            disablePortal
                                            options={InvoiceDays}
                                            getOptionLabel={(option) => option || ""}
                                            isOptionEqualToValue={(option, value) => getDayLabel(option) === value}
                                            value={getDayLabel(invoiceConfig?.advanced_rental_invoice_generation_day)}
                                            onChange={(e, value) => onFieldChange("advanced_rental_invoice_generation_day", value || '')}
                                            disabled={!editMode}
                                            slotProps={{ listbox: { sx: { maxHeight: 155 } } }}
                                            renderInput={(params) => {
                                                const selectedLabel = params?.inputProps?.value || "";
                                                return (
                                                    <Tooltip title={selectedLabel.length > 23 ? selectedLabel : ""} placement="bottom" arrow ><TextField  {...params} label="Invoice Generation Day" margin="normal" size="small" slotProps={{ inputLabel: { shrink: true } }} /></Tooltip>)
                                            }}
                                        />
                                    </Grid>
                                    <Grid item size={{ md: 2.5, xs: 12 }}>
                                        <Autocomplete
                                            disablePortal
                                            options={['Same Month', 'Previous Month']}
                                            slotProps={{ listbox: { sx: { maxHeight: 155 } } }}
                                            value={invoiceConfig.advanced_falling_months || 'Same Month'}
                                            onChange={(e, value) =>
                                                onFieldChange(
                                                  "advanced_falling_months",
                                                  value || "Same Month"
                                                )
                                              }
                                            disabled={!editMode}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Falling Month"
                                                    margin="normal"
                                                    size="small"
                                                    slotProps={{ inputLabel: { shrink: true } }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item size={{ md: 0.2, xs: 12 }} sx={{ marginTop: '-5px'}}> 
                                        <Divider orientation="vertical" sx={{ height: 50, borderRightWidth: 2,  borderColor: '#c0c3c6' }} /> 
                                    </Grid>
                                    <Grid size={{ md: 2.5, xs: 12 }}>
                                        <TextField
                                            id="advanced_rental_invoice_due_day"
                                            value={invoiceConfig?.advanced_rental_invoice_due_day}
                                            onChange={(e, value) => onFieldChange("advanced_rental_invoice_due_day", e.target.value.trimStart())}
                                            label="Invoice Due Day"
                                            variant="outlined"
                                            size="small"
                                            margin="normal"
                                            slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 2 } }}
                                            fullWidth
                                            disabled={!editMode}
                                        />
                                    </Grid>
                                    <Grid size={{ md: 4, xs: 12 }}>
                                        <FormControlLabel
                                            control={<Checkbox disableRipple size="small" color="primary" disabled={!editMode} onChange={(e) => onFieldChange("advanced_rental_invoice_is_due_day_last_day_month", e.target.checked)} checked={!!invoiceConfig?.advanced_rental_invoice_is_due_day_last_day_month} />}
                                            label="Invoice Due Day falls at last day of the Billing Month"
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container size={{ md: 12, xs: 12 }} spacing={2} className='invoice-config-grid'>
                                    <Grid size={{ md: 12, xs: 12 }}>
                                        <Typography className='invoice-config-text'>Other Fields</Typography>
                                    </Grid>
                                    <Grid item size={{ md: 12, xs: 12 }} sx={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                                        <FormGroup className="disabled-checkbox-wrapper" sx={{ ml: "5px" }}>
                                            <Box sx={{ alignItems: "center", }}>
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                    <span className="check-item-span">Rental Billing Cycle</span>
                                                    {moment(leaseBasicDetails?.start_date).date() === 31 && (
                                                        <Tooltip title="'As Per Lease Period' is disabled because the lease starts on the 31st, which does not exist in all months." placement="right" arrow>
                                                            <InfoOutlinedIcon sx={{ fontSize: '15px', color: 'text.primary', cursor: 'pointer' }} />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                                <RadioGroup
                                                    row
                                                    value={invoiceConfig?.is_rental_billing_cycle === true ? true : false}
                                                    onChange={(e) => onFieldChange("is_rental_billing_cycle", e.target.value)}
                                                    sx={{ mt: '1px' }}
                                                >
                                                    <FormControlLabel value={true} control={<Radio disableRipple size="small" />} label="Monthly" disabled={!editMode} />
                                                    <FormControlLabel value={false} control={<Radio disableRipple size="small" />} label="As Per Lease Period" disabled={!editMode || moment(leaseBasicDetails?.start_date).date() === 31} />
                                                </RadioGroup>
                                            </Box>
                                        </FormGroup>
                                        {invoiceConfig?.is_rental_billing_cycle === false ?
                                            <Grid item size={{ md: 1.6, xs: 12 }} sx={{ mt: 2.5 }}>
                                                <Autocomplete
                                                    disablePortal
                                                    options={InvoiceDays}
                                                    getOptionLabel={(option) => option || ""}
                                                    isOptionEqualToValue={(option, value) => getDayLabel(option) === value}
                                                    value={getDayLabel(invoiceConfig?.billing_generation_day || leaseStartDay)}
                                                    onChange={(e, value) => onFieldChange("billing_generation_day", value || '')}
                                                    disabled={!editMode}
                                                    sx={{
                                                        width: '100%',
                                                        "& .MuiAutocomplete-clearIndicator": { padding: "0px" },
                                                        "& .MuiAutocomplete-clearIndicator svg": { fontSize: "11px", margin: 0 },
                                                        "& .MuiAutocomplete-popupIndicator": { padding: "0px" },
                                                        "& .MuiAutocomplete-popupIndicator svg": { fontSize: "14px" },
                                                    }}
                                                    slotProps={{ listbox: { sx: { maxHeight: 155 } } }}
                                                    renderInput={(params) => {
                                                        const selectedLabel = params?.inputProps?.value || "";
                                                        return (
                                                            <Tooltip title={selectedLabel.length > 23 ? selectedLabel : ""} placement="bottom" arrow >
                                                                <TextField
                                                                    {...params}
                                                                    label="Billing Day"
                                                                    margin="normal"
                                                                    size="small"
                                                                    sx={{
                                                                        "& .MuiInputLabel-root": { fontSize: "15px", },
                                                                        "& .MuiInputBase-root": { height: "25px", fontSize: "14px", },
                                                                        "& .MuiAutocomplete-input": { padding: "0px !important", minWidth: '90px !important' },
                                                                    }}
                                                                    slotProps={{ inputLabel: { shrink: true } }} />
                                                            </Tooltip>
                                                        )
                                                    }}
                                                />
                                            </Grid> : null}
                                    </Grid>
                                    <Grid item size={{ md: 3, xs: 12 }}>
                                        <Autocomplete
                                            disablePortal
                                            options={bank}
                                            getOptionLabel={(option) => option?.name || ""}
                                            noOptionsText={dropDownloader ? "Loading..." : "No Option Available"}
                                            value={(bank || []).find((opt) => opt?.id === invoiceConfig?.rental_invoice_bank?.id) || null}
                                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                            onChange={(e, value) => onFieldChange("rental_invoice_bank", value || '')}
                                            disabled={!editMode}
                                            slotProps={{ listbox: { sx: { maxHeight: 155 } } }}
                                            renderInput={(params) => {
                                                const selectedLabel = params?.inputProps?.value || "";
                                                return (
                                                    <Tooltip title={selectedLabel.length > 23 ? selectedLabel : ""} placement="bottom" arrow ><TextField  {...params} label="Invoice Bank" margin="normal" size="small"
                                                        slotProps={{ inputLabel: { shrink: true }, input: { ...params?.InputProps, endAdornment: (<>{dropDownloader ? (<CircularProgress color="inherit" size={20} />) : null} {params?.InputProps?.endAdornment} </>) } }} /></Tooltip>
                                                )
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={{ md: 3, xs: 12 }}>
                                        <TextField
                                            id="rental_advance_duration"
                                            value={invoiceConfig?.rental_advance_duration ? invoiceConfig?.rental_advance_duration : ''}
                                            onChange={(e, value) => regexConstants.isValidNumeric.test(e.target.value) ? onFieldChange("rental_advance_duration", e.target.value) : e.preventDefault()}
                                            label="Rental Advance Duration (Month)"
                                            variant="outlined"
                                            size="small"
                                            margin="normal"
                                            slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 2 } }}
                                            fullWidth
                                            disabled={!editMode}
                                        />
                                    </Grid>
                                    <Grid size={{ md: 3, xs: 12 }}>
                                        <TextField
                                            id="reference_number"
                                            value={invoiceConfig?.reference_number}
                                            onChange={(e, value) => onFieldChange("reference_number", e.target.value.trimStart())}
                                            label="Reference Number"
                                            variant="outlined"
                                            size="small"
                                            margin="normal"
                                            slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 50 } }}
                                            fullWidth
                                            disabled={!editMode}
                                        />
                                    </Grid>
                                    <Grid size={{ md: 3, xs: 12 }}>
                                        <TextField
                                            id="qis_number"
                                            value={invoiceConfig?.qis_number}
                                            onChange={(e, value) => onFieldChange("qis_number", e.target.value.trimStart())}
                                            label="QIS Number"
                                            variant="outlined"
                                            size="small"
                                            margin="normal"
                                            slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 50 } }}
                                            fullWidth
                                            disabled={!editMode}
                                        />
                                    </Grid><Grid size={{ md: 3, xs: 12 }}>
                                        <TextField
                                            id="sales_order_number"
                                            value={invoiceConfig?.sales_order_number}
                                            onChange={(e, value) => onFieldChange("sales_order_number", e.target.value.trimStart())}
                                            label="Sales Order Number"
                                            variant="outlined"
                                            size="small"
                                            margin="normal"
                                            slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 50 } }}
                                            fullWidth
                                            disabled={!editMode}
                                        />
                                    </Grid>
                                    <Grid size={{ md: 3, xs: 12 }}>
                                        <LocalizationProvider dateAdapter={AdapterMoment}>
                                            <DatePicker
                                                label="Remarks Date"
                                                value={invoiceConfig?.remarks_date ? moment(invoiceConfig.remarks_date) : null}
                                                onChange={(value) => {
                                                    onFieldChange("remarks_date", value);
                                                    setError({ ...error, remarks_date: "" })
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        InputLabelProps: { shrink: true },
                                                        size: "small",
                                                        margin: "normal",
                                                        error: error?.remarks_date || false,
                                                        helperText: error?.remarks_date || '',
                                                        onFocus: () => setError({ ...error, remarks_date: "" })
                                                    },
                                                }}
                                                sx={{ width: "100%" }}
                                                format={fieldDateFormat}
                                                disabled={!editMode}
                                            />
                                        </LocalizationProvider>
                                    </Grid>
                                    {editMode ?
                                        <Grid size={{ md: 3, xs: 12 }}>
                                            <AutocompleteCreatable
                                                options={[]}
                                                label='Email CC'
                                                value={Array.isArray(invoiceConfig?.rental_email_cc) ? invoiceConfig?.rental_email_cc : invoiceConfig?.rental_email_cc ? invoiceConfig.rental_email_cc.split(',').map(e => e.trim()).filter(Boolean) : []}
                                                paramsKey='rental_email_cc'
                                                onFieldChange={(e, paramsKey, newValue) => onFieldChange('rental_email_cc', newValue)}
                                                multiple={true}
                                                noOptionsText={'No options, please type to create new'}
                                                placeholder={!editMode ? '' : 'Enter Email(s)'}
                                                error={error?.rental_email_cc}
                                                helperText={error?.rental_email_cc || ''}
                                                isDisabled={!editMode}
                                                useLimitTags={1}

                                            />
                                        </Grid> :
                                        <Grid size={{ md: 3, xs: 12 }}>
                                            <Tooltip title={emailValue?.length > 30 ? emailValue : ""} arrow>
                                                <TextField
                                                    id="rental_email_cc"
                                                    value={Array.isArray(invoiceConfig?.rental_email_cc) ? invoiceConfig?.rental_email_cc.join(', ') : invoiceConfig?.rental_email_cc || ''}
                                                    label="Email CC"
                                                    variant="outlined"
                                                    size="small"
                                                    margin="normal"
                                                    slotProps={{ inputLabel: { shrink: true } }}
                                                    fullWidth
                                                    disabled={true}
                                                />
                                            </Tooltip>
                                        </Grid>}
                                    {editMode ?
                                        <Grid item size={{ md: 12, xs: 12 }}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                minRows={2}
                                                maxRows={10}
                                                size="small"
                                                label="Description"
                                                slotProps={{ inputLabel: { shrink: true } }}
                                                value={invoiceConfig.description}
                                                onChange={(e, value) => onFieldChange('description', e.target.value.trimStart())}
                                                disabled={!editMode}
                                            />
                                        </Grid> : <Grid item size={{ md: 12, xs: 12 }}>
                                            <LessMoreTextFieldComp label='Description' value={invoiceConfig?.description} />
                                        </Grid>}
                                    {editMode ?
                                        <Grid item size={{ md: 12, xs: 12 }}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                minRows={2}
                                                maxRows={10}
                                                size="small"
                                                label="Remarks"
                                                slotProps={{ inputLabel: { shrink: true } }}
                                                value={invoiceConfig.rental_remarks}
                                                onChange={(e, value) => onFieldChange('rental_remarks', e.target.value.trimStart())}
                                                disabled={!editMode}
                                            />
                                        </Grid>
                                        : <Grid item size={{ md: 12, xs: 12 }}>
                                            <LessMoreTextFieldComp label='Remarks' value={invoiceConfig?.rental_remarks} />
                                        </Grid>}
                                </Grid>
                            </Grid>
                        </div>
                        {editMode && (
                            <Box className="invoice-config-actions-bar">
                                <Button className='cancel-btn' onClick={() => { getInvoiceConfig(); setEditMode(false); handleItemClicked('Cancel Button') }} variant="outlined" size="small" disabled={isLoading}>
                                    Cancel
                                </Button>
                                <Button className='btn-primary' onClick={() => { handleSave(); handleItemClicked('Save Button') }} variant="contained" size="small" disabled={isLoading}>
                                    {isLoading ? <CircularProgress size={24} /> : 'Save'}
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Paper>

        </>
    );
};

export default InvoiceConfiguration;
